// src/hooks/queries/usePortal.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { portalService } from '@/services/portal';
import { toast } from '@/components/ui';
import type {
  PortalConfig,
  PortalDashboard,
  PortalTicket,
  PortalTicketCreate,
  PortalComentario,
  PortalComentarioCreate,
  PortalConversacion,
  PortalConversacionDetalle,
  PortalConversacionCreate,
  PortalMensajeCreate,
  PortalArticuloList,
  PortalArticulo,
  PortalCategoria,
  PortalPerfil,
  PortalPerfilUpdate,
  PortalCambiarPassword,
  PortalEmpresaResumen,
  PortalListResponse,
} from '@/types';

// === CONFIG Y DASHBOARD ===

export function usePortalConfig() {
  return useQuery<PortalConfig>({
    queryKey: ['portal', 'config'],
    queryFn: () => portalService.getConfig(),
  });
}

export function usePortalDashboard() {
  return useQuery<PortalDashboard>({
    queryKey: ['portal', 'dashboard'],
    queryFn: () => portalService.getDashboard(),
    refetchInterval: 60000, // Refrescar cada minuto
  });
}

export function usePortalEmpresas() {
  return useQuery<PortalEmpresaResumen[]>({
    queryKey: ['portal', 'empresas'],
    queryFn: () => portalService.getEmpresas(),
  });
}

// === TICKETS ===

export function usePortalTickets(params?: { empresa_id?: string; estado?: string }) {
  return useQuery<PortalListResponse<PortalTicket>>({
    queryKey: ['portal', 'tickets', params],
    queryFn: () => portalService.listTickets(params),
  });
}

export function usePortalTicket(id: string | undefined) {
  return useQuery<PortalTicket>({
    queryKey: ['portal', 'tickets', id],
    queryFn: () => portalService.getTicket(id!),
    enabled: !!id,
  });
}

export function usePortalComentarios(ticketId: string | undefined) {
  return useQuery<PortalComentario[]>({
    queryKey: ['portal', 'tickets', ticketId, 'comentarios'],
    queryFn: () => portalService.getComentarios(ticketId!),
    enabled: !!ticketId,
  });
}

export function useCrearPortalTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: PortalTicketCreate) => portalService.createTicket(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal', 'tickets'] });
      queryClient.invalidateQueries({ queryKey: ['portal', 'dashboard'] });
      toast.success('Ticket creado correctamente');
    },
  });
}

export function useAgregarPortalComentario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ticketId, data }: { ticketId: string; data: PortalComentarioCreate }) =>
      portalService.addComentario(ticketId, data),
    onSuccess: (_, { ticketId }) => {
      queryClient.invalidateQueries({ queryKey: ['portal', 'tickets', ticketId, 'comentarios'] });
      queryClient.invalidateQueries({ queryKey: ['portal', 'tickets', ticketId] });
      toast.success('Comentario agregado');
    },
  });
}

// === CONVERSACIONES ===

export function usePortalConversaciones(params?: { empresa_id?: string; estado?: string }) {
  return useQuery<PortalListResponse<PortalConversacion>>({
    queryKey: ['portal', 'conversaciones', params],
    queryFn: () => portalService.listConversaciones(params),
  });
}

export function usePortalConversacion(id: string | undefined) {
  return useQuery<PortalConversacionDetalle>({
    queryKey: ['portal', 'conversaciones', id],
    queryFn: () => portalService.getConversacion(id!),
    enabled: !!id,
    refetchInterval: 10000, // Refrescar cada 10s para simular tiempo real
  });
}

export function useCrearPortalConversacion() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: PortalConversacionCreate) => portalService.createConversacion(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal', 'conversaciones'] });
      queryClient.invalidateQueries({ queryKey: ['portal', 'dashboard'] });
      toast.success('Conversacion iniciada');
    },
  });
}

export function useEnviarPortalMensaje() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ conversacionId, data }: { conversacionId: string; data: PortalMensajeCreate }) =>
      portalService.sendMensaje(conversacionId, data),
    onSuccess: (_, { conversacionId }) => {
      queryClient.invalidateQueries({ queryKey: ['portal', 'conversaciones', conversacionId] });
    },
  });
}

// === CONOCIMIENTO ===

export function usePortalArticulos(params?: { query?: string; categoria_id?: string }) {
  return useQuery<PortalListResponse<PortalArticuloList>>({
    queryKey: ['portal', 'conocimiento', params],
    queryFn: () => portalService.searchArticulos(params),
  });
}

export function usePortalCategorias() {
  return useQuery<PortalCategoria[]>({
    queryKey: ['portal', 'conocimiento', 'categorias'],
    queryFn: () => portalService.getCategorias(),
  });
}

export function usePortalArticulo(id: string | undefined) {
  return useQuery<PortalArticulo>({
    queryKey: ['portal', 'conocimiento', id],
    queryFn: () => portalService.getArticulo(id!),
    enabled: !!id,
  });
}

export function useMarcarArticuloUtil() {
  return useMutation({
    mutationFn: ({ id, util }: { id: string; util: boolean }) =>
      portalService.markArticuloUtil(id, util),
    onSuccess: () => {
      toast.success('Gracias por tu feedback');
    },
  });
}

// === PERFIL ===

export function usePortalPerfil() {
  return useQuery<PortalPerfil>({
    queryKey: ['portal', 'perfil'],
    queryFn: () => portalService.getPerfil(),
  });
}

export function useActualizarPortalPerfil() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: PortalPerfilUpdate) => portalService.updatePerfil(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portal', 'perfil'] });
      toast.success('Perfil actualizado');
    },
  });
}

export function useCambiarPortalPassword() {
  return useMutation({
    mutationFn: (data: PortalCambiarPassword) => portalService.changePassword(data),
    onSuccess: () => {
      toast.success('Contrasena cambiada correctamente');
    },
  });
}
